import { memo, useState } from 'react'
import type { FC } from 'react'
import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { callAPI } from '@/api'
import { callLocales } from '@/i18n'
import { createGrid } from '@/styles'
import { Layout, HeadTag } from '@/components'
import { HeaderMarquee } from '@dy/commons/components'
import { FaqsCategories, QuestionsList } from 'components/faqs'

type TypeFaqQuestion = {
  question: string,
  answer: string,
  hash: string
}

type TypeFaqCategory = {
  name: string,
  seo: any,
  slug: any,
  faqs: TypeFaqQuestion[]
}

type TypeFaqs = TypeFaqCategory[]


const Main = styled.main`
  ${createGrid()}
  min-height: 95vh;
`

const SEO = {
  title: 'Hellomamma | Faqs',
  description: 'Faqs page'
}

interface onLoad {
  onLoad?: any,
  isLoading?: boolean,
  user?: any,
  isInvalid?: any,
  isAuthorize?: any,
  data: TypeFaqs,
  locales: any,
}

const TRANSLATIONS = {
  'es': 'Preguntas frecuentes',
  'en': 'FAQs',
  'it': 'Preguntas frecuentes',
  'fr': 'Questions fréquentes'
}

const FaqsIndexPage: NextPage<onLoad> & { Layout?:FC } = memo(({ data }) => {
  const { query, locale } = useRouter() || { query: {}, locale: '' }

  const [activeIndex, setActiveIndex] = useState(null)
  const [activeCategory, setActiveCategory] = useState(false)
  const [activeQuestion, setActiveQuestion] = useState(false)

  return (
    <>
      {/* @ts-ignore */}
      <HeadTag data={SEO} jsonld={{ data: query?.slug ? data.filter((item) => item.slug[locale] === query.slug[0])[0] : data[0], pageType: 'faqs' }} slug={''} pathname='faqs/' />
      <HeaderMarquee text={TRANSLATIONS[locale]} />
      <Main>
        {data &&
          <>
            <FaqsCategories categories={data} activeIndex={activeIndex} activeCategory={activeCategory} activeQuestion={activeQuestion}/>
            {data.map((category, idx) => (
              <QuestionsList key={idx} data={category} idx={idx} setActiveIndex={setActiveIndex} setActiveCategory={setActiveCategory} setActiveQuestion={setActiveQuestion}/>
            ))}
          </>
        }
      </Main>
    </>
  )
})

export async function getStaticPaths({ locales }) {
  const [data, error] = await callAPI({ type: 'faqs' })

  if (error) return { paths: [], fallback: false }

  const initialPath = [{ params: { slug: [], locale: locales[0] }}]
  const localePaths = []
  locales.map((locale) => data.map((faqCategory) => {
    const slug = faqCategory.slug[locale]
    if(slug) {
      const params = { slug: [slug] }
      localePaths.push({ params, locale })
    }
  }))

  // return { paths: [...initialPath], fallback: 'blocking' }
  return { paths: [...initialPath, ...localePaths], fallback: 'blocking' }
}

export async function getStaticProps({ locale }) {
  const [data, error] = await callAPI({ type: 'faqs', params: { locale }})
  const [locales, layoutLocales]  = await callLocales({ type: 'home', locale })
  const [layoutData, layoutError] = await callAPI({ type: 'layout',  params: { locale } })

  if(error || layoutError) return { notFound: true }

  return {
    props: {
      data,
      locales,
      layoutLocales,
      layout: layoutData
    },
    revalidate: 120,
  }
}

FaqsIndexPage.Layout = Layout
export default FaqsIndexPage
